import { skipToken, useInfiniteQuery } from '@tanstack/react-query';

import { Automations } from '@air/api';
import { type Succeeded } from '@air/server-sdk';
import { callAirApi } from '@air/utils-api';

export type AutomationRun = Succeeded<typeof Automations.listRuns>['data'][number];

export const PRIVATE_AUTOMATION_RUNS_LIST_KEY = 'PRIVATE_AUTOMATION_RUNS_LIST';

export type PrivateAutomationRunsListKey = [
  typeof PRIVATE_AUTOMATION_RUNS_LIST_KEY,
  UsePrivateAutomationRunsListParams,
];

export const getPrivateAutomationRunsListKey = (
  params: UsePrivateAutomationRunsListParams,
): PrivateAutomationRunsListKey => [PRIVATE_AUTOMATION_RUNS_LIST_KEY, params];

export type UsePrivateAutomationRunsListParams = Omit<
  NonNullable<Parameters<typeof Automations.listRuns>[0]>,
  'workspaceId' | 'automationId' | 'cursor'
> & {
  workspaceId: string | undefined;
  automationId: string | undefined;
};

export type UsePrivateAutomationRunsListOptions = {
  enabled?: boolean;
};

export const usePrivateAutomationRunsList = (
  params: UsePrivateAutomationRunsListParams,
  options?: UsePrivateAutomationRunsListOptions,
) => {
  const { workspaceId, automationId, limit } = params;

  const queryFn =
    !workspaceId || !automationId
      ? skipToken
      : async ({ pageParam }: { pageParam: string }) =>
          callAirApi(() =>
            Automations.listRuns({
              workspaceId,
              automationId,
              limit,
              cursor: pageParam || undefined,
            }),
          );

  return useInfiniteQuery({
    queryKey: getPrivateAutomationRunsListKey(params),
    queryFn,
    getNextPageParam: (lastPage) => (lastPage.pagination?.hasMore ? (lastPage.pagination.cursor ?? null) : null),
    initialPageParam: '',
    enabled: !!workspaceId && !!automationId && options?.enabled,
  });
};
